import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  type SelectChangeEvent,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useInsightsDashboard } from '../../../contexts/InsightsDashboardContext';

const EMPTY_ARRAY: never[] = [];

interface FilterBarProps {
  availableOptions?: {
    teams: string[];
    sectors: string[];
    clients: string[];
    sources: string[];
    states?: string[];
  };
  sourceLabels?: Record<string, string>;
}

const FilterBar = ({ availableOptions, sourceLabels = {} }: FilterBarProps) => {
  const { state, dispatch, saveDefaults } = useInsightsDashboard();

  const handleDateChange =
    (type: 'startDate' | 'endDate') => (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: 'SET_DATE_RANGE',
        payload: {
          ...state.dateRange,
          [type]: event.target.value ? new Date(event.target.value) : null,
        },
      });
    };

  const handleMultiSelectChange =
    (type: 'SET_TEAMS' | 'SET_SECTORS' | 'SET_CLIENTS' | 'SET_SOURCES' | 'SET_STATES') =>
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string[];
      dispatch({
        type,
        payload: value,
      });
    };

  const handleSaveDefaults = async () => {
    await saveDefaults();
  };

  const formatDate = (date: Date | null) => {
    if (!date) {
      return '';
    }
    const d = new Date(date);
    let month = `${d.getMonth() + 1}`;
    let day = `${d.getDate()}`;
    const year = d.getFullYear();

    if (month.length < 2) {
      month = `0${month}`;
    }
    if (day.length < 2) {
      day = `0${day}`;
    }

    return [year, month, day].join('-');
  };

  const options = availableOptions || {
    teams: [],
    sectors: [],
    clients: [],
    sources: [],
    states: [],
  };
  const safeStates = options.states || EMPTY_ARRAY;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={formatDate(state.dateRange.startDate)}
            onChange={handleDateChange('startDate')}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={formatDate(state.dateRange.endDate)}
            onChange={handleDateChange('endDate')}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="teams-label">Teams</InputLabel>
            <Select
              labelId="teams-label"
              multiple
              value={state.teams}
              onChange={handleMultiSelectChange('SET_TEAMS')}
              input={<OutlinedInput id="select-multiple-teams" label="Teams" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {options.teams.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="sectors-label">Sectors</InputLabel>
            <Select
              labelId="sectors-label"
              multiple
              value={state.sectors}
              onChange={handleMultiSelectChange('SET_SECTORS')}
              input={<OutlinedInput id="select-multiple-sectors" label="Sectors" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {options.sectors.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Second Row */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="clients-label">Clients</InputLabel>
            <Select
              labelId="clients-label"
              multiple
              value={state.clients}
              onChange={handleMultiSelectChange('SET_CLIENTS')}
              input={<OutlinedInput id="select-multiple-clients" label="Clients" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {options.clients.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="sources-label">Sources</InputLabel>
            <Select
              labelId="sources-label"
              multiple
              value={state.sources}
              onChange={handleMultiSelectChange('SET_SOURCES')}
              input={<OutlinedInput id="select-multiple-sources" label="Sources" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={sourceLabels[value] || value} size="small" />
                  ))}
                </Box>
              )}
            >
              {options.sources.map((name) => (
                <MenuItem key={name} value={name}>
                  {sourceLabels[name] || name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="states-label">States</InputLabel>
            <Select
              labelId="states-label"
              multiple
              value={state.states}
              onChange={handleMultiSelectChange('SET_STATES')}
              input={<OutlinedInput id="select-multiple-states" label="States" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {safeStates.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveDefaults}
            sx={{
              backgroundColor: 'greyContrast',
              color: 'primary.contrastText',
            }}
          >
            Save as Default
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FilterBar;
